import * as React from 'react';

import { type MaybeHTTPError, reportException } from '@gitbook/util-debug';
import { useLazyRef } from '@gitbook/util-react-hooks';

import type {
    ComputedCommandsSection,
    PaletteCommandDef,
    PaletteDef,
    PaletteGroupDef,
} from '../types';
import { DEFAULT_COMMANDS_PRIORITY, filterAndSortCommands, sortAndFilterSections } from './util';

export interface ComputedPaletteCommands {
    /** Unique identifier to represent the set of comamnds */
    listId: number | string;

    /** Sections to render in the UI for the commans */
    sections: ComputedCommandsSection[];

    /** All commands that are distributed in "sections" */
    items: PaletteCommandDef[];

    /** Indicate if we are loading other commands */
    areMoreLoading: boolean;

    /** Indicate if there was an error computing commands */
    error?: MaybeHTTPError;
}

/**
 * Compute all commands matching a query using a palette state.
 * This function gives us the commands that are available right now
 * and a boolean indicating whether there are more inflight.
 */
export function useComputePaletteCommands(palette: PaletteDef): ComputedPaletteCommands {
    const [areMoreLoading, setAreMoreLoading] = React.useState(false);

    const groupCacheRef = useLazyRef<WeakMap<PaletteGroupDef, ComputedCommandsSection>>(
        () => new WeakMap()
    );
    const recentRequestsRef = React.useRef<Promise<any> | null>(null);

    return React.useMemo(() => {
        const available: ComputedCommandsSection[] = [];
        const inFlight: Array<Promise<void>> = [];

        let error: MaybeHTTPError | undefined;

        palette.groups.forEach((group) => {
            let groupResult: ComputedCommandsSection | Promise<void> | undefined;
            try {
                groupResult = computeCommandsGroup(palette, group);
            } catch (err) {
                error = err;
                return;
            }

            if (groupResult instanceof Promise) {
                // Use cache if available to continue showing previous results until we fetch new ones.
                const cacheResult =
                    group.showCommandsWhileComputing !== false
                        ? groupCacheRef.current.get(group)
                        : undefined;
                if (cacheResult) {
                    available.push(cacheResult);
                }

                inFlight.push(groupResult);
            } else if (groupResult) {
                groupCacheRef.current.set(group, groupResult);
                available.push(groupResult);
            }
        });

        const availableSections = sortAndFilterSections(available);

        const availableCommands = availableSections.reduce<PaletteCommandDef[]>((acc, section) => {
            acc.push(...section.data);
            return acc;
        }, []);

        if (inFlight.length > 0) {
            setAreMoreLoading(true);
            /**
             * We store a reference to the requests in this pass as the most recently fired requests
             * When these requests resolve, we disable loading indicator only if they were the "recent" ones fired
             * so we have no incorrect loading state changes due to older requests being resolved late.
             */
            const currentInFlightRequests = (recentRequestsRef.current = Promise.all(inFlight));
            currentInFlightRequests
                .then(() => {
                    if (currentInFlightRequests === recentRequestsRef.current) {
                        setAreMoreLoading(false);
                        recentRequestsRef.current = null;
                    }
                })
                .catch((failed) => reportException(new Error(failed)));
        }

        return {
            listId: palette.hackScrollListId ?? Date.now(),
            items: availableCommands,
            sections: availableSections,
            areMoreLoading,
            error,
        };
    }, [palette.hackScrollListId, palette.groups, palette.query, areMoreLoading, groupCacheRef]);
}

/**
 * Compute the commands for a given group.
 */
function computeCommandsGroup(
    palette: PaletteDef,
    group: PaletteGroupDef
): ComputedCommandsSection | Promise<void> | undefined {
    try {
        const commands =
            typeof group.commands === 'function' ? group.commands(palette) : group.commands;

        // The group is considered static when it uses a function to compute commands
        // AND the function requires the `palette` argument.
        const isStaticGroup = typeof group.commands !== 'function' || group.commands.length === 0;

        // If the list is static, we do our own filtering
        if (isStaticGroup) {
            const { commands: data, priority } = filterAndSortCommands(
                palette.query.text,
                commands,
                group.priority
            );

            return {
                group,
                priority,
                data,
            };
        }

        return {
            group,
            priority: group.priority || DEFAULT_COMMANDS_PRIORITY,
            data: commands,
        };
    } catch (errPromise) {
        /**
         * Our suspense based cache throws a promise when we do "group.commands(palette)"
         * However since we do not want to suspend rendering in our palette, we are explicitly
         * catching it and making sure it does NOT reach a Suspense Boundary higher in the tree.
         */
        if (errPromise instanceof Promise) {
            return errPromise;
        }
        throw errPromise;
    }
}
